import { useState } from 'react';
import { BodyText, Div, Flex, Gutter, Icon, SideNavigation as SideNavigationTelia } from '@gaiads/telia-react-component-library';
import { SideNavigationItemType } from '@gaiads/telia-react-component-library/build/types/components/SideNavigation/SideNavigationItem/SideNavigationItem';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { routes } from './routes';

import styles from './SideNavigation.module.scss';

interface SideNavigationParamTypes {
  id?: string;
}

interface SideNavigationProps {
  isNotDesktop?: boolean;
}

export const SideNavigation = (props: SideNavigationProps) => {
  const { isNotDesktop = false } = props;
  const organizationId = useParams<SideNavigationParamTypes>().id || undefined;
  const history = useHistory();
  const { t } = useTranslation('sideNavigation');

  const urlWithCustomerIdRegex = 'customer/[A-Za-z0-9-]+';

  /* State needed only for mobile navigation menu */
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<boolean>(false);

  const parseCompanyHomePage = (pathname: string) => {
    return pathname.match(urlWithCustomerIdRegex)?.[0] ?? '';
  };

  const activateNavigationItem = (itemPath: string) => {
    const homePage = parseCompanyHomePage(history.location.pathname);
    return history.location.pathname.includes(`${homePage}${itemPath}`);
  };

  const navigationItems: SideNavigationItemType[] = [
    {
      label: t('companyInformation'),
      active: activateNavigationItem(routes.companyInformation),
      onClick: () => history.push(`/${parseCompanyHomePage(history.location.pathname)}${routes.companyInformation}`),
      key: 1,
      iconName: 'premises',
    },
    {
      label: t('orders'),
      active: activateNavigationItem(routes.orders),
      onClick: () => history.push(`/${parseCompanyHomePage(history.location.pathname)}${routes.orders}`),
      key: 2,
      iconName: 'delivery',
    },
    {
      label: t('devices'),
      active: activateNavigationItem(routes.devices),
      onClick: () => history.push(`/${parseCompanyHomePage(history.location.pathname)}${routes.devices}`),
      key: 3,
      iconName: 'smartphone',
    },
    {
      label: t('users'),
      active: activateNavigationItem(routes.users),
      onClick: () => history.push(`/${parseCompanyHomePage(history.location.pathname)}${routes.users}`),
      key: 4,
      iconName: 'end-user',
    },
    {
      label: t('userGroups'),
      active: activateNavigationItem(routes.userGroups),
      onClick: () => history.push(`/${parseCompanyHomePage(history.location.pathname)}${routes.userGroups}`),
      key: 5,
      iconName: 'people',
    },
  ];

  const render = () => {
    let content;
    if (!organizationId) {
      content = <></>;
    } else if (!isNotDesktop) {
      // Desktop navigation
      content = <SideNavigationTelia navigationItems={navigationItems} />;
    } else {
      // Mobile navigation
      content = (
        <Div
          className={
            !mobileDropdownOpen
              ? `${styles['side-navigation-mobile-nav-menu']} ${styles['side-navigation-mobile-nav-menu--closed']}`
              : styles['side-navigation-mobile-nav-menu']
          }
        >
          <Flex margin={{ left: 'xs' }} spaceBetween={true} occupyVerticalSpace={false} onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}>
            <Flex centeredVertically={true}>
              <Icon name="menu" size="md" />
              <Gutter size="layout-2" />
              <BodyText>{t('mobileNavigationMenu')}</BodyText>
            </Flex>
            <Icon name={mobileDropdownOpen ? 'chevron-up' : 'chevron-down'} size="md" />
          </Flex>
          <Flex hidden={!mobileDropdownOpen} directionColumn margin={{ top: 'sm' }}>
            {navigationItems.map(navigationItem => {
              const baseClass = styles['side-navigation-mobile-nav-menu__menu-items__item'];
              const modifierClass = navigationItem.active
                ? styles['side-navigation-mobile-nav-menu__menu-items__item--active']
                : styles['side-navigation-mobile-nav-menu__menu-items__item--not-active'];
              const classNames = `${baseClass} ${modifierClass}`;
              return (
                <Div
                  key={navigationItem.key}
                  onClick={() => {
                    setMobileDropdownOpen(false);
                    navigationItem.onClick();
                  }}
                  className={classNames}
                >
                  <BodyText>{navigationItem.label}</BodyText>
                </Div>
              );
            })}
          </Flex>
        </Div>
      );
    }

    return content;
  };

  return render();
};
